import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const link = 'text-[#2c2c2c] hover:text-[#005a9c] transition-colors';
const subLink =
  'block py-2 px-3 text-sm text-[#5c5c5c] hover:bg-[#f0f6fb] hover:text-[#005a9c] rounded-sm';
const subLinkBold = `${subLink} font-medium text-[#2c2c2c]`;
const groupLabel = 'px-3 py-1.5 text-[11px] font-semibold text-[#5c6d7e] uppercase tracking-[0.08em]';

export default function SiteHeader() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(null); // 'svc' | 'sol' | 'res' | null

  return (
    <header className="sticky top-0 z-[100] bg-white/95 backdrop-blur-sm border-b border-[#c5d4e3] shadow-[0_1px_0_rgba(255,255,255,0.8)]">
      <div className="max-w-[1140px] mx-auto px-5 h-[56px] flex items-center justify-between gap-4">
        <button
          type="button"
          className="md:hidden p-2 -ml-2 text-[#2c2c2c]"
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        <Link to="/" className="flex items-center gap-2.5 shrink-0" onClick={() => setOpen(false)}>
          <img src={require('../img/rakfort.jpg')} alt="RakFort" className="h-8 w-8 rounded-sm" />
          <span className="font-semibold text-[#2c2c2c] tracking-tight text-[1.05rem]">RakFort</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-[0.9375rem]">
          {/* Services — mega */}
          <div className="relative group">
            <button type="button" className={`flex items-center gap-0.5 px-3 py-2 rounded-sm ${link}`}>
              Services
              <ChevronDown className="w-4 h-4 opacity-60" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-[200]">
              <div className="bg-white border border-[#c5d4e3] shadow-lg rounded-sm overflow-hidden min-w-[560px] grid grid-cols-2 divide-x divide-[#e8eef4]">
                <div className="p-3 space-y-0.5">
                  <p className={groupLabel}>Portfolio</p>
                  <Link to="/services" className={subLinkBold}>
                    All services
                  </Link>
                  <Link to="/services/ai-consulting" className={subLink}>
                    AI Consulting
                  </Link>
                  <Link to="/services/risk-management" className={subLink}>
                    Risk management
                  </Link>
                  <Link to="/services/human-resources" className={subLink}>
                    Human resources
                  </Link>
                </div>
                <div className="p-3 space-y-0.5 bg-[#f8fbfd]">
                  <p className={groupLabel}>AI Security</p>
                  <Link to="/services/ai-security" className={subLinkBold}>
                    Overview
                  </Link>
                  <Link to="/scanfort" className={`${subLink} pl-6`}>
                    ScanFort — assessment
                  </Link>
                  <Link to="/riskfort" className={`${subLink} pl-6`}>
                    RiskFort — risk management
                  </Link>
                  <p className={`${groupLabel} pt-3`}>Human Trust</p>
                  <Link to="/services/human-trust" className={subLinkBold}>
                    Human Trust Services
                  </Link>
                  <Link to="/services/human-trust" className={`${subLink} pl-6`}>
                    SimStrike — AI attack simulations
                  </Link>
                  <Link to="/services/human-trust" className={`${subLink} pl-6`}>
                    AI Trust & Readiness Programme
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Solutions — mega */}
          <div className="relative group">
            <button type="button" className={`flex items-center gap-0.5 px-3 py-2 rounded-sm ${link}`}>
              Solutions
              <ChevronDown className="w-4 h-4 opacity-60" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-[200]">
              <div className="bg-white border border-[#c5d4e3] shadow-lg rounded-sm overflow-hidden min-w-[520px] grid grid-cols-2 divide-x divide-[#e8eef4]">
                <div className="p-3 space-y-0.5">
                  <p className={groupLabel}>Start here</p>
                  <Link to="/solutions/ai-security" className={subLinkBold}>
                    Solutions overview
                  </Link>
                  <p className={`${groupLabel} pt-3`}>Governance</p>
                  <Link to="/solutions/governance" className={subLink}>
                    Governance hub
                  </Link>
                  <Link to="/aigov" className={`${subLink} pl-6`}>
                    AI governance — AiGov
                  </Link>
                  <Link to="/scanfort" className={`${subLink} pl-6`}>
                    Cybersecurity assessment — ScanFort
                  </Link>
                </div>
                <div className="p-3 space-y-0.5 bg-[#f8fbfd]">
                  <p className={groupLabel}>Security for AI</p>
                  <Link to="/solutions/security-for-ai" className={subLinkBold}>
                    Security for AI hub
                  </Link>
                  <Link to="/modelfort" className={`${subLink} pl-6`}>
                    ModelFort
                  </Link>
                  <Link to="/guardfort" className={`${subLink} pl-6`}>
                    GuardFort
                  </Link>
                  <p className={`${groupLabel} pt-3`}>Human Trust</p>
                  <Link to="/solutions/security-for-ai" className={subLinkBold}>
                    Human Trust hub
                  </Link>
                  <Link to="/solutions/trustscore" className={`${subLink} pl-6`}>
                    TrustScore
                  </Link>
                  <Link to="/solutions/deepstrike" className={`${subLink} pl-6`}>
                    DeepStrike
                  </Link>
                  <Link to="/solutions/copilotready" className={`${subLink} pl-6`}>
                    AgentReady
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Research — dropdown */}
          <div className="relative group">
            <button type="button" className={`flex items-center gap-0.5 px-3 py-2 rounded-sm ${link}`}>
              Research
              <ChevronDown className="w-4 h-4 opacity-60" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-[200]">
              <div className="bg-white border border-[#c5d4e3] shadow-lg rounded-sm overflow-hidden min-w-[220px]">
                <div className="p-3 space-y-0.5">
                  <Link to="/research?tab=applications" className={subLink}>Applications</Link>
                  <Link to="/research?tab=projects" className={subLink}>Our Projects</Link>
                  <Link to="/research?tab=publications" className={subLink}>Scientific Publications</Link>
                  <Link to="/research?tab=news" className={subLink}>News</Link>
                </div>
              </div>
            </div>
          </div>
          <Link to="/use-cases" className={`px-3 py-2 rounded-sm ${link}`}>
            Use cases
          </Link>
          <Link to="/aboutus" className={`px-3 py-2 rounded-sm ${link}`}>
            About
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/resources"
            className="hidden sm:inline text-[0.9375rem] text-[#5c5c5c] hover:text-[#005a9c] px-2"
          >
            Resources
          </Link>
          <button
            type="button"
            onClick={() => navigate('/contactus')}
            className="text-[0.9375rem] font-medium px-3 py-1.5 border border-[#b8c9d9] text-[#2c2c2c] hover:border-[#005a9c] hover:text-[#005a9c] rounded-sm bg-white transition-colors"
          >
            Contact
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[#c5d4e3] bg-white px-5 py-4 space-y-1 text-[0.9375rem] max-h-[min(80vh,560px)] overflow-y-auto">
          <button
            type="button"
            className="flex items-center justify-between w-full py-2 text-left text-[#2c2c2c]"
            onClick={() => setMobile(mobile === 'svc' ? null : 'svc')}
          >
            Services
            <ChevronDown className={`w-4 h-4 transition-transform ${mobile === 'svc' ? 'rotate-180' : ''}`} />
          </button>
          {mobile === 'svc' && (
            <div className="pl-3 pb-2 space-y-1 border-l border-[#e8e8e8] ml-1 text-[0.9rem]">
              <Link to="/services" className="block py-1.5 text-[#005a9c] font-medium" onClick={() => setOpen(false)}>
                All services
              </Link>
              <Link to="/services/ai-consulting" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                AI Consulting
              </Link>
              <p className="text-xs text-[#737373] pt-2 pb-0.5">AI Security</p>
              <Link to="/services/ai-security" className="block py-1.5 text-[#005a9c]" onClick={() => setOpen(false)}>
                Overview
              </Link>
              <Link to="/scanfort" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                ScanFort
              </Link>
              <Link to="/riskfort" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                RiskFort
              </Link>
              <p className="text-xs text-[#737373] pt-2 pb-0.5">Human Trust</p>
              <Link to="/services/human-trust" className="block py-1.5 text-[#005a9c]" onClick={() => setOpen(false)}>
                Human Trust Services
              </Link>
              <Link to="/services/human-trust" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                SimStrike — AI attack simulations
              </Link>
              <Link to="/services/human-trust" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                AI Trust & Readiness Programme
              </Link>
              <Link to="/services/risk-management" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Risk management
              </Link>
              <Link to="/services/human-resources" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Human resources
              </Link>
            </div>
          )}
          <button
            type="button"
            className="flex items-center justify-between w-full py-2 text-left text-[#2c2c2c]"
            onClick={() => setMobile(mobile === 'sol' ? null : 'sol')}
          >
            Solutions
            <ChevronDown className={`w-4 h-4 transition-transform ${mobile === 'sol' ? 'rotate-180' : ''}`} />
          </button>
          {mobile === 'sol' && (
            <div className="pl-3 pb-2 space-y-1 border-l border-[#e8e8e8] ml-1 text-[0.9rem]">
              <Link
                to="/solutions/ai-security"
                className="block py-1.5 text-[#005a9c] font-medium"
                onClick={() => setOpen(false)}
              >
                Solutions overview
              </Link>
              <p className="text-xs text-[#737373] pt-2 pb-0.5">Governance</p>
              <Link to="/solutions/governance" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Governance hub
              </Link>
              <Link to="/aigov" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                AI governance — AiGov
              </Link>
              <Link to="/scanfort" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                Cybersecurity assessment — ScanFort
              </Link>
              <p className="text-xs text-[#737373] pt-2 pb-0.5">Security for AI</p>
              <Link
                to="/solutions/security-for-ai"
                className="block py-1.5 text-[#5c5c5c]"
                onClick={() => setOpen(false)}
              >
                Security for AI hub
              </Link>
              <Link to="/modelfort" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                ModelFort
              </Link>
              <Link to="/guardfort" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                GuardFort
              </Link>
              <p className="text-xs text-[#737373] pt-2 pb-0.5">Human Trust</p>
              <Link to="/solutions/security-for-ai" className="block py-1.5 text-[#005a9c]" onClick={() => setOpen(false)}>
                Human Trust hub
              </Link>
              <Link to="/solutions/trustscore" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                TrustScore
              </Link>
              <Link to="/solutions/deepstrike" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                DeepStrike
              </Link>
              <Link to="/solutions/copilotready" className="block py-1.5 text-[#5c5c5c] pl-2" onClick={() => setOpen(false)}>
                AgentReady
              </Link>
            </div>
          )}
          <button
            type="button"
            className="flex items-center justify-between w-full py-2 text-left text-[#2c2c2c]"
            onClick={() => setMobile(mobile === 'res' ? null : 'res')}
          >
            Research
            <ChevronDown className={`w-4 h-4 transition-transform ${mobile === 'res' ? 'rotate-180' : ''}`} />
          </button>
          {mobile === 'res' && (
            <div className="pl-3 pb-2 space-y-1 border-l border-[#e8e8e8] ml-1 text-[0.9rem]">
              <Link to="/research?tab=applications" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Applications
              </Link>
              <Link to="/research?tab=projects" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Our Projects
              </Link>
              <Link to="/research?tab=publications" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                Scientific Publications
              </Link>
              <Link to="/research?tab=news" className="block py-1.5 text-[#5c5c5c]" onClick={() => setOpen(false)}>
                News
              </Link>
            </div>
          )}
          <Link to="/use-cases" className="block py-2 text-[#2c2c2c]" onClick={() => setOpen(false)}>
            Use cases
          </Link>
          <Link to="/aboutus" className="block py-2 text-[#2c2c2c]" onClick={() => setOpen(false)}>
            About
          </Link>
          <Link to="/resources" className="block py-2 text-[#5c5c5c]" onClick={() => setOpen(false)}>
            Resources
          </Link>
        </div>
      )}
    </header>
  );
}
